/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useCallback, useState } from "react";
import {
  IUserSubscriptionModel,
  PaymentPeriod,
  TimeHelper,
  useDispatch,
  UserStore,
  UserSubscriptionStatus,
} from "@nf/common";
import { ROUTES } from "@nf/constants";
import cx from "classnames";
import dynamic from "next/dynamic";
import { Trans, useTranslation } from "next-i18next";

import Checkmark from "resources/icons/checkmark.svg";

import styles from "./SettingsSubscriptionContainer.module.scss";

const SettingsSubscriptionButton = dynamic(
  () => import("components/Settings/SettingsSubscriptionButton")
);

const Web_subscription_change_confirm_description = dynamic(
  () =>
    import(
      "components/TranslationsContainers/Web_subscription_change_confirm_description"
    )
);

const RegretSwitchDialog = dynamic(
  () =>
    import(
      "components/CancellationDialog/steps/Confirmation/RegretSwitchDialog"
    )
);

import {
  useCancelSubscriptionSwitch,
  useIsAuthenticated,
  useRouter,
} from "hooks";
import { PaymentDetailType } from "../../../../PaymentDetailType";
import { useUncancelSubscription } from "../../hooks";
import PartnerInformation from "./PartnerInformation";
import DunningAlert from "./DunningAlert";
import SubscriptionTrialInfo from "./SubscriptionTrialInfo";
import { PaymentError } from "components/PaymentError";
import { useUnpauseSubscription } from "../../../../CancellationDialog/hooks/useUnpauseSubscription";

interface ISettingsSubscriptionContainerProps {
  hasActiveSub?: boolean;
  subscription: IUserSubscriptionModel;
  onCancel?: (subscription: IUserSubscriptionModel) => void;
  onCancelPause?: () => void;
}

const getStatusClassName = ({
  IsActive,
  IsExpired,
}: IUserSubscriptionModel): string => {
  if (IsExpired) {
    return styles.statusExpired;
  }
  return IsActive ? styles.statusActive : styles.statusInactive;
};

const SubscriptionsButtonContainer: React.FC<
  ISettingsSubscriptionContainerProps
> = ({ subscription, hasActiveSub, onCancel, onCancelPause }) => {
  const { t } = useTranslation("translations");
  const { isRegisteredViaCampaign } = useIsAuthenticated();
  const dispatch = useDispatch();
  const { push } = useRouter();
  const {
    Id,
    IsActive,
    IsCancelled,
    ExpirationDateLabel,
    Partner,
    SwitchToOffer,
    Period,
    CanBeCancelled,
    CanBeChanged,
    IsPendingPause,
    IsPaused,
  } = subscription;

  const [isRegretSwitchDialogOpen, setIsRegretSwitchDialogOpen] =
    useState(false);

  const changeSubscription = useCallback(() => {
    push(ROUTES.PAYMENT_CHANGE_SUBSCRIPTION);
  }, [push]);

  const {
    mutateAsync: uncancelSubscription,
    isLoading: isUncancellingSubscription,
    error: uncancelSubscriptionError,
  } = useUncancelSubscription();

  const uncancel = () => {
    uncancelSubscription(Id).then(() => {
      dispatch(UserStore.Actions.getUserSubscriptions());
    });
  };

  const {
    mutateAsync: unpauseSubscription,
    isLoading: isUnpausing,
    error: unpauseError,
  } = useUnpauseSubscription();

  const unpause = useCallback(() => {
    unpauseSubscription({ id: `${Id}` }).then(() => {
      onCancelPause?.();
      dispatch(UserStore.Actions.getUserSubscriptions());
    });
  }, [Id, unpauseSubscription]);

  const {
    mutateAsync: cancelSwitchSubscription,
    isLoading: isCancellingSwitch,
    error: cancelSwitchSubscriptionError,
  } = useCancelSubscriptionSwitch();

  const openRegretSwitch = () => {
    setIsRegretSwitchDialogOpen(true);
  };

  const closeRegretSwitch = () => {
    setIsRegretSwitchDialogOpen(false);
  };

  const onConfirmRegretSwitch = () => {
    cancelSwitchSubscription().then(() => {
      closeRegretSwitch();
      dispatch(UserStore.Actions.getUserSubscriptions());
    });
  };

  if (IsActive || IsPaused) {
    return (
      <div className={styles.buttons}>
        {SwitchToOffer && (
          <>
            <p>
              <Web_subscription_change_confirm_description
                date={ExpirationDateLabel}
                price={SwitchToOffer.Price}
                period={SwitchToOffer.Period}
                currency={SwitchToOffer.Currency}
              />
            </p>

            <SettingsSubscriptionButton
              variant="subtle"
              onClick={openRegretSwitch}
            >
              {t("web_regret_subscription_button")}
            </SettingsSubscriptionButton>

            {isRegretSwitchDialogOpen && (
              <RegretSwitchDialog
                isVisible={isRegretSwitchDialogOpen}
                isLoading={isCancellingSwitch}
                onCancel={closeRegretSwitch}
                onConfirm={onConfirmRegretSwitch}
              />
            )}
          </>
        )}

        {CanBeChanged && (
          <>
            {Period === PaymentPeriod.Month && (
              <Trans
                t={t}
                parent="p"
                i18nKey={
                  isRegisteredViaCampaign
                    ? "web_subscription_change_description_aeldresagen"
                    : "web_subscription_change_description"
                }
                components={{
                  bold: <strong />,
                }}
              />
            )}
            <SettingsSubscriptionButton
              variant="primary"
              onClick={changeSubscription}
            >
              {t("web_subscription_change_button")}
            </SettingsSubscriptionButton>
          </>
        )}

        {cancelSwitchSubscriptionError?.Message && (
          <PaymentError
            error={cancelSwitchSubscriptionError.Message.toString()}
          />
        )}

        {CanBeCancelled && (
          <SettingsSubscriptionButton
            variant="subtle"
            onClick={() => onCancel?.(subscription)}
          >
            {t("web_subscription_button_cancel")}
          </SettingsSubscriptionButton>
        )}

        {(IsPendingPause || IsPaused) && (
          <>
            <SettingsSubscriptionButton
              variant="subtle"
              state={isUnpausing ? "loading" : "default"}
              onClick={unpause}
            >
              {t("web_my_account_subscription_button_cancel_pause")}
            </SettingsSubscriptionButton>

            {unpauseError?.Message && (
              <PaymentError error={unpauseError.Message?.toString()} />
            )}
          </>
        )}
      </div>
    );
  }

  if (!Partner && !hasActiveSub && IsCancelled) {
    return (
      <>
        <div>{t("web_subscription_subscription_restart_into")}</div>

        {uncancelSubscriptionError?.Message && (
          <PaymentError error={uncancelSubscriptionError.Message.toString()} />
        )}

        <SettingsSubscriptionButton
          variant="primary"
          state={isUncancellingSubscription ? "loading" : "default"}
          onClick={uncancel}
        >
          {t("web_subscription_restart_subscription")}
        </SettingsSubscriptionButton>
      </>
    );
  }

  return null;
};

export const SettingsSubscriptionContainer = ({
  hasActiveSub,
  subscription,
  onCancel,
  onCancelPause,
}: ISettingsSubscriptionContainerProps) => {
  const { t } = useTranslation("translations");
  const isActive = subscription.IsActive;
  const isPaused = subscription.IsPaused;
  const isPendingPause = subscription.IsPendingPause;
  const isInDunning = subscription.Status === UserSubscriptionStatus.IN_DUNNING;
  const isTrial = subscription.IsTrial;
  const expirationDate = subscription.ExpirationDateLabel;
  const canBeCancelled = subscription.CanBeCancelled;

  return (
    <>
      {isInDunning && <DunningAlert />}

      {!isActive && !isPaused && (
        <div className={styles.container}>
          <Checkmark className={styles.checkmark} />

          <span>
            {t("web_subscription_cancel_peroid")}
            <span className="bold">{expirationDate}</span>
          </span>
        </div>
      )}

      <div className={styles.container}>
        <div className={cx(styles.status, getStatusClassName(subscription))} />

        <div className={styles.content}>
          {subscription.Partner ? (
            <PartnerInformation name={subscription.Partner.Name} />
          ) : (
            <>
              {!subscription.SwitchToOffer && (
                <>
                  {canBeCancelled && (
                    <PaymentDetailType
                      subscription={subscription}
                      className={styles.PaymentDetailType}
                    />
                  )}

                  {!isPaused && !isTrial && (
                    <div className={styles.item}>
                      <span className="bold">
                        {canBeCancelled
                          ? isActive
                            ? t("web_subscription_next_renewal")
                            : t("web_subscription_subscription_ends")
                          : t("web_subscription_expiry")}
                        :
                      </span>
                      {`${expirationDate}.`}
                    </div>
                  )}

                  <div className={styles.item}>
                    <span className="bold">{t("web_subscription_status")}</span>
                    <span>
                      {t(
                        `web_subscription_status_${
                          isInDunning || isTrial
                            ? UserSubscriptionStatus.ACTIVE
                            : subscription.Status
                        }`
                      )}
                    </span>
                  </div>

                  {isTrial && (
                    <div className={styles.item}>
                      <SubscriptionTrialInfo subscription={subscription} />
                    </div>
                  )}
                </>
              )}

              {isPendingPause && (
                <span>
                  {t("web_my_account_subscription_pause_pending", {
                    date: TimeHelper.format(
                      new Date(subscription.PauseAt || ""),
                      "DD. MMMM YYYY"
                    ).toLowerCase(),
                  })}
                </span>
              )}

              {isPaused && (
                <span>{t("web_my_account_subscription_pause_active")}</span>
              )}

              <SubscriptionsButtonContainer
                hasActiveSub={hasActiveSub}
                subscription={subscription}
                onCancel={onCancel}
                onCancelPause={onCancelPause}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SettingsSubscriptionContainer;
